// noinspection ExceptionCaughtLocallyJS

import { createAsyncThunk } from '@reduxjs/toolkit';
import { IRequestDetails } from 'interfaces/ResourceRequests';
import {
  addRequest,
  getMaxRequestNumber,
  updateRequest,
  getIncidents,
  ApiIncidentsFilters,
} from '../../../services/requestEndPoints';
import apiFetch from 'services/apiFetch';
import { RequestFormData } from '../../../pages/Dashboard/ResourceRequest/hooks/useRequestForm';
import { RootState } from 'store';
import { getAgencyCount } from 'services/agencyEndPoints';
import { IGetAgencyCount } from 'interfaces/request.interface';

import { IncidentsInterface } from 'interfaces/incident.interface';
import { v4 as uuidv4 } from 'uuid';

export const createRequest = createAsyncThunk(
  'request/createRequest',
  async (data: RequestFormData, { getState }) => {
    try {
      const { token } = getState() as RootState;
      const user = token.user;
      const payload: IRequestDetails = {
        missionType: data.requestDetails.missionType,
        requestNumber: data.requestDetails.requestNumber,
        incidentId: data.requestDetails.incidentId,
        palmettoResourceRequest: data.requestDetails.palmettoResourceRequest || 0,
        onSceneContactPerson: data.requestDetails.onSceneContactPerson,
        onSceneContactPhone: data.requestDetails.onSceneContactPhone,
        date: data.requestDetails.date,
        departmentName: data.requestDetails.departmentName,
        contactPerson: data.requestDetails.contactPerson,
        contactPhone: data.requestDetails.contactPhone,
        stagingLocation: data.requestDetails.stagingLocation,
        destination: data.requestDetails.destination ? data.requestDetails.destination : '',
        notes: data.requestDetails.notes,
        deleted: 0,
        archived: 0,
        emailBody: data.emailBody.replace(/\r\n|\r|\n/g, '<br>'),
        ambulance: data.resources.ambulance,
        personnel: data.resources.personnel,
        regions: data.regions,
        userId: user?.id,
        groupId: uuidv4(),
      };
      const endpoint = addRequest(payload);
      const response = await apiFetch<IRequestDetails>(endpoint).then((res) => res?.data || null);
      if (response && response.id) {
        return 'Success';
      }
      throw Error('Something went wrong!');
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('createRequest catch:', message);
      throw Error(message);
    }
  },
);
export const changeRequest = createAsyncThunk(
  'request/changeRequest',
  async (data: RequestFormData, { getState }) => {
    try {
      const { token } = getState() as RootState;
      const user = token.user;
      const payload: IRequestDetails = {
        id: data.id,
        incidentId: data.requestDetails.incidentId,
        palmettoResourceRequest: data.requestDetails.palmettoResourceRequest || 0,
        onSceneContactPerson: data.requestDetails.onSceneContactPerson,
        onSceneContactPhone: data.requestDetails.onSceneContactPhone,
        missionType: data.requestDetails.missionType,
        requestNumber: data.requestDetails.requestNumber,
        date: data.requestDetails.date,
        departmentName: data.requestDetails.departmentName,
        contactPerson: data.requestDetails.contactPerson,
        contactPhone: data.requestDetails.contactPhone,
        stagingLocation: data.requestDetails.stagingLocation,
        destination: data.requestDetails.destination ? data.requestDetails.destination : '',
        notes: data.requestDetails.notes,
        deleted: 0,
        archived: 0,
        emailBody: data.emailBody.replace(/\r\n|\r|\n/g, '<br>'),
        ambulance: data.resources.ambulance,
        personnel: data.resources.personnel,
        regions: data.regions,
        userId: user?.id,
        groupId: uuidv4(),
      };
      const endpoint = updateRequest(payload);
      const response = await apiFetch<IRequestDetails>(endpoint).then((res) => res?.data || null);

      if (response && response.id) {
        return 'Success';
      }
      throw Error('Something went wrong');
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('changeRequest catch:', message);
      throw Error(message);
    }
  },
);

export const fetchAgencyCount = createAsyncThunk('agencies/count', async (county: string) => {
  try {
    const endpoint = getAgencyCount(county);
    const response = await apiFetch<IGetAgencyCount>(endpoint).then((res) => res?.data || null);

    if (response) {
      return response.count;
    } else {
      throw Error('Something went wrong');
    }
  } catch (error) {
    let message = 'Something went wrong!';
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    console.log('fetchAgencyCount catch:', message);
    throw Error(message);
  }
});

export const fetchMaxRequestNumber = createAsyncThunk('request/maxrequestnumber', async () => {
  try {
    const endpoint = getMaxRequestNumber();
    const response = await apiFetch<{ max: number }>(endpoint).then((res) => res?.data || null);
    if (response) {
      return response.max + 1;
    } else {
      throw Error('Something went wrong!');
    }
  } catch (error) {
    let message = 'Something went wrong!';
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    console.log('fetchMaxRequestNumber catch:', message);
    throw Error(message);
  }
});

export const fetchRequestNumberForIncident = createAsyncThunk(
  'request/requestNumberForIncident',
  async (incidentId: string) => {
    try {
      const endpoint = getMaxRequestNumber(incidentId);
      const response = await apiFetch<{ max: number }>(endpoint).then((res) => res?.data || null);
      if (response && response.max !== null) {
        return response.max + 1;
      } else {
        return 1;
      }
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('fetchRequestNumberForIncident catch:', message);
      return 1;
    }
  },
);

export const fetchIncidents = createAsyncThunk('incident/fetchallincidents', async () => {
  try {
    const filters: ApiIncidentsFilters = {
      archived: 0,
    };
    const endpoint = getIncidents(filters);
    const response = await apiFetch<IncidentsInterface>(endpoint).then((res) => res?.data || null);
    if (response) {
      return response;
    } else {
      throw Error('Something went wrong');
    }
  } catch (error) {
    let message = 'Something went wrong!';
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    console.log('fetchIncidents:', message);
    throw Error(message);
  }
});
